import {useEffect,useState} from 'react';
import supabase from '../supabase/supabase';
import {getTrackingId} from '../utils/tracking';

const SLOT_STORAGE_KEY='glitchy_dynamic_slot';
const SLOT_EVENT='tracking_slot_updated';

const normalizeSlot=(value)=> {
  const parsed=Number.parseInt(value,10);
  return Number.isNaN(parsed) ? value : parsed;
};

const getErrorMessage=async (error)=> {
  if (error instanceof Error && error.message) {
    const context=error.context;

    if (context && typeof context.clone==='function') {
      const responseText=await context.clone().text().catch(()=> '');

      if (responseText) {
        return `${error.message}: ${responseText}`;
      }
    }

    return error.message;
  }

  if (typeof error==='string') {
    return error;
  }

  if (error && typeof error==='object') {
    const details=Object.entries(error)
      .map(([key,value])=> {
        const formattedValue=typeof value==='string' ? value : JSON.stringify(value);
        return `${key}: ${formattedValue}`;
      })
      .join(',');

    return details || 'Unknown slot leasing error.';
  }

  return 'Unknown slot leasing error.';
};

const notifySlotUpdate=(detail)=> {
  window.dispatchEvent(new CustomEvent(SLOT_EVENT,{detail}));
};

const getSlotFromResponse=(data)=> {
  const result=Array.isArray(data) ? data[0] : data;

  if (result?.success===false) {
    throw new Error(
      typeof result.error==='string' ? result.error : JSON.stringify(result.error)
    );
  }

  const returnedSlot=result?.slot ?? result?.assigned_slot;

  if (returnedSlot===undefined || returnedSlot===null) {
    throw new Error(`Invalid slot response: ${JSON.stringify(data)}`);
  }

  return normalizeSlot(returnedSlot);
};

const requestSlotFromEdgeFunction=async (transactionId)=> {
  const {data,error}=await supabase.functions.invoke('leguprx-webhook',{
    body: {
      transaction_id: transactionId,
      url: window.location.href,
      userAgent: navigator.userAgent
    }
  });

  if (error) {
    throw error;
  }

  return getSlotFromResponse(data);
};

const requestSlotFromDatabase=async (transactionId)=> {
  const {data,error}=await supabase.rpc('lease_dynamic_slot_v2',{
    p_meta: {
      url: window.location.href,
      userAgent: navigator.userAgent
    },
    p_transaction_id: transactionId,
    p_ttl_hours: 24
  });

  if (error) {
    throw error;
  }

  return getSlotFromResponse(data);
};

export const leaseDynamicSlot=async (useMockFallback=false)=> {
  const transactionId=getTrackingId();

  if (transactionId==='direct_organic') {
    notifySlotUpdate({
      slot: null,
      error: null,
      loading: false,
      skipped: true
    });

    return {
      slot: null,
      error: null,
      skipped: true
    };
  }

  const cachedSlot=sessionStorage.getItem(SLOT_STORAGE_KEY);

  if (cachedSlot!==null) {
    const slot=normalizeSlot(cachedSlot);

    notifySlotUpdate({
      slot,
      error: null,
      loading: false,
      skipped: false
    });

    return {
      slot,
      error: null,
      skipped: false
    };
  }

  notifySlotUpdate({
    slot: null,
    error: null,
    loading: true,
    skipped: false
  });

  try {
    let slot;

    try {
      slot=await requestSlotFromEdgeFunction(transactionId);
    } catch (edgeError) {
      console.warn(
        'Edge Function slot request failed. Trying direct RPC fallback.',
        edgeError
      );

      slot=await requestSlotFromDatabase(transactionId);
    }

    sessionStorage.setItem(SLOT_STORAGE_KEY,String(slot));

    notifySlotUpdate({
      slot,
      error: null,
      loading: false,
      skipped: false
    });

    return {
      slot,
      error: null,
      skipped: false
    };
  } catch (error) {
    const message=await getErrorMessage(error);

    console.error('Slot leasing error:',error);

    if (useMockFallback) {
      const slot=Math.floor(Math.random() * 250)+1;
      const simulatedError=`Simulated (real error: ${message})`;

      sessionStorage.setItem(SLOT_STORAGE_KEY,String(slot));

      notifySlotUpdate({
        slot,
        error: simulatedError,
        loading: false,
        skipped: false
      });

      return {
        slot,
        error: null,
        skipped: false
      };
    }

    const slotError=`Unable to reserve your tracking slot: ${message}`;

    notifySlotUpdate({
      slot: null,
      error: slotError,
      loading: false,
      skipped: false
    });

    return {
      slot: null,
      error: slotError,
      skipped: false
    };
  }
};

export const useSlotLease=()=> {
  const [slot,setSlot]=useState(()=> {
    const cachedSlot=sessionStorage.getItem(SLOT_STORAGE_KEY);
    return cachedSlot===null ? null : normalizeSlot(cachedSlot);
  });
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [skipped,setSkipped]=useState(false);

  useEffect(()=> {
    const syncSlot=(event)=> {
      const cachedSlot=sessionStorage.getItem(SLOT_STORAGE_KEY);
      const detail=event.detail || {};

      setSlot(
        detail.slot ?? (cachedSlot===null ? null : normalizeSlot(cachedSlot))
      );
      setLoading(Boolean(detail.loading));
      setError(detail.error ?? null);
      setSkipped(Boolean(detail.skipped));
    };

    window.addEventListener(SLOT_EVENT,syncSlot);

    return ()=> {
      window.removeEventListener(SLOT_EVENT,syncSlot);
    };
  },[]);

  return {
    slot,
    loading,
    error,
    skipped,
    setLoading,
    setError
  };
};